import './styles/graphic.scss';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});

const drawMap = (container, isMobile) => {
  mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

  const sendHeightIfPym = () => {
    if (window && window.pymChild && typeof window.pymChild.sendHeight === 'function') {
      window.pymChild.sendHeight();
    }
  };

  // screen size config
  const optionsSm = {
    initZoom: 1,
    minZoom: 1,
    maxZoom: 4,
    center: [-179, 37],
    maxBounds: [[-280, -50], [-90, 75]],
  };

  const optionsLg = {
    initZoom: 1,
    minZoom: 1,
    maxZoom: 5,
    center: [-100, 25], // Center shifted east to show more Asia
    maxBounds: [[-270, -50], [-90, 75]], // Moderate bounds allowing some zoom out and Asia coverage
  };

  const options = isMobile ? optionsSm : optionsLg;

  const map = new mapboxgl.Map({
    container: container.node(),
    style: 'mapbox://styles/cnndigital/cmfplqr7k00ef01rf3w8z9d5w',
    center: options.center,
    zoom: options.initZoom,
    maxZoom: options.maxZoom,
    minZoom: options.minZoom,
    maxBounds: options.maxBounds,
    dragPan: {
      maxBounds: options.maxBounds, // Constrain the map panning
    },
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  map.scrollZoom.disable();

  if (!isMobile) {
    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
    });
    map.addControl(nav, 'top-left');
  }

  // Log the initial zoom level
  // console.log(`Initial zoom level: ${map.getZoom()}`);

  // Add an event listener to log the zoom level whenever it changes
  map.on('zoom', () => {
    // console.log(`Zoom level changed to: ${map.getZoom()}`);
  });

  // Log style load events and errors
  map.on('style.load', () => {
    // console.log('Style loaded');
  });

  map.on('error', (e) => {
    console.error('Error:', e.error);
  });

  // Function to fetch and process data
  const fetchDataAndAddLayer = () => {
    fetch('https://ix.cnn.io/data/sea-temperatures/sea_surface_temp_anomalies.json')
      .then(response => response.json())
      .then((data) => {
        // console.log(data);
        const timestamp = data.noaa_posted_est_str;
        const released_date = data.noaa_file_posted_est_str;
        document.getElementById('timestamp').textContent = `Notes: Latest observation from ${timestamp}. Values derived from five-kilometer satellite sea surface temperature anomalies data.

`;
        sendHeightIfPym();
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  };

  map.on('load', () => {
    // Add Pacific sea surface temperature raster source
    map.addSource('pacific-sea-temps', {
      type: 'raster',
      url: 'mapbox://cnndigital.sea_temps_all_oceans',
    });

    // Add Pacific sea surface temperature layer
    map.addLayer({
      id: 'pacific-sea-temps-layer',
      type: 'raster',
      source: 'pacific-sea-temps',
      paint: {
        'raster-opacity': 0.8,
      },
    });

    // Move country labels above the raster layer
    if (map.getLayer('country-labels')) {
      map.moveLayer('country-labels');
    }

    // On mobile, hide city/place/poi labels but keep country labels visible
    if (isMobile) {
      const styleLayers = (map.getStyle() && map.getStyle().layers) ? map.getStyle().layers : [];
      styleLayers.forEach((layer) => {
        const sourceLayer = layer['source-layer'];
        if (
          layer.type === 'symbol'
          && sourceLayer
          && (
            sourceLayer.includes('place_label')
            || sourceLayer.includes('poi_label')
            || sourceLayer.includes('airport_label')
            || sourceLayer.includes('settlement')
          )
          && layer.id !== 'country-labels'
        ) {
          map.setLayoutProperty(layer.id, 'visibility', 'none');
        }
      });

      // Shift UNITED STATES label west on mobile only
      if (map.getLayer('country-labels')) {
        const existingTextOffset = map.getLayoutProperty('country-labels', 'text-offset');
        map.setLayoutProperty('country-labels', 'text-offset', [
          'case',
          ['==', ['get', 'name_en'], 'United States'],
          ['literal', [-6, 0]],
          existingTextOffset || ['literal', [0, 0]],
        ]);
      }

      if (map.getLayer('country-labels')) {
        const existingTextOffset = map.getLayoutProperty('country-labels', 'text-offset');
        map.setLayoutProperty('country-labels', 'text-offset', [
          'case',
          ['==', ['get', 'name_en'], 'Canada'],
          ['literal', [-5, 0]],
          existingTextOffset || ['literal', [0, 0]],
        ]);
      }

      if (map.getLayer('country-labels')) {
        const existingTextOffset = map.getLayoutProperty('country-labels', 'text-offset');
        map.setLayoutProperty('country-labels', 'text-offset', [
          'case',
          ['==', ['get', 'name_en'], 'China'],
          ['literal', [5, -3]],
          existingTextOffset || ['literal', [0, 0]],
        ]);
      }
    }


    fetchDataAndAddLayer();
    sendHeightIfPym();
    map.once('idle', sendHeightIfPym);
  });
};

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
  } = props;

  container.html(''); // Clear previous content
  container.append('div')
    .attr('id', 'map')
    .style('width', `${initialWidth}px`)
    .style('height', `${initialHeight}px`);

  drawMap(container.select('#map'), isMobile); // Pass the D3 selection of the map div
}
